/**
 * Civilhealth Healthcare - Status Colors Design Tokens
 * WCAG AA Compliant - High Contrast Icon & Background Colors
 *
 * Rule: Icons must ALWAYS have strong contrast against their background
 * Use white icons on colored backgrounds, or dark icons on light backgrounds
 */

// Status Colors - Each status has its own color system
export const statusColors = {
  // Pending Status - Amber/Yellow
  pending: {
    bg: '#FEF3C7',           // Amber 100 - Light background
    icon: '#D97706',          // Amber 700 - Dark icon for light bg
    iconOnDark: '#FFFFFF',    // White icon for colored bg
    solid: '#F59E0B',         // Amber 500 - Solid color
    light: '#FCD34D',         // Amber 300
    dark: '#B45309',          // Amber 700
    text: '#92400E',          // Amber 800 - Text color
    border: '#F59E0B',        // Amber 500 - Border
  },

  // Accepted/Confirmed Status - Green
  accepted: {
    bg: '#DCFCE7',            // Green 100 - Light background
    icon: '#15803D',          // Green 700 - Dark icon for light bg
    iconOnDark: '#FFFFFF',    // White icon for colored bg
    solid: '#10B981',         // Green 500
    light: '#34D399',         // Green 300
    dark: '#059669',          // Green 600
    text: '#166534',          // Green 800 - Text color
    border: '#10B981',        // Green 500 - Border
  },

  // In Progress Status - Blue
  inProgress: {
    bg: '#DBEAFE',            // Blue 100 - Light background
    icon: '#1D4ED8',          // Blue 700 - Dark icon for light bg
    iconOnDark: '#FFFFFF',    // White icon for colored bg
    solid: '#3B82F6',         // Blue 500
    light: '#60A5FA',         // Blue 300
    dark: '#1E40AF',          // Blue 800
    text: '#1E3A8A',          // Blue 900 - Text color
    border: '#3B82F6',        // Blue 500 - Border
  },

  // Completed Status - Green
  completed: {
    bg: '#DCFCE7',            // Green 100 - Light background
    icon: '#166534',          // Green 800 - Dark icon for light bg
    iconOnDark: '#FFFFFF',    // White icon for colored bg
    solid: '#10B981',         // Green 500
    light: '#34D399',         // Green 300
    dark: '#047857',          // Green 700
    text: '#14532D',          // Green 900 - Text color
    border: '#10B981',        // Green 500 - Border
  },

  // Cancelled/Rejected Status - Red
  cancelled: {
    bg: '#FEE2E2',            // Red 100 - Light background
    icon: '#DC2626',          // Red 600 - Dark icon for light bg
    iconOnDark: '#FFFFFF',    // White icon for colored bg
    solid: '#EF4444',         // Red 500
    light: '#F87171',         // Red 300
    dark: '#B91C1C',          // Red 700
    text: '#991B1B',          // Red 800 - Text color
    border: '#EF4444',        // Red 500 - Border
  },

  // Error Status - Red
  error: {
    bg: '#FEE2E2',            // Red 100
    icon: '#DC2626',          // Red 600
    iconOnDark: '#FFFFFF',    // White icon
    solid: '#EF4444',         // Red 500
    text: '#991B1B',          // Red 800
    border: '#EF4444',        // Red 500
  },

  // Warning Status - Orange
  warning: {
    bg: '#FFEDD5',            // Orange 100
    icon: '#EA580C',          // Orange 600
    iconOnDark: '#FFFFFF',    // White icon
    solid: '#F97316',         // Orange 500
    text: '#9A3412',          // Orange 800
    border: '#F97316',        // Orange 500
  },

  // Info Status - Blue
  info: {
    bg: '#DBEAFE',            // Blue 100
    icon: '#1D4ED8',          // Blue 700
    iconOnDark: '#FFFFFF',    // White icon
    solid: '#3B82F6',         // Blue 500
    text: '#1E3A8A',          // Blue 900
    border: '#3B82F6',        // Blue 500
  },

  // Success Status - Green
  success: {
    bg: '#DCFCE7',            // Green 100
    icon: '#15803D',          // Green 700
    iconOnDark: '#FFFFFF',    // White icon
    solid: '#10B981',         // Green 500
    text: '#166534',          // Green 800
    border: '#10B981',        // Green 500
  },

  // Neutral/Default Status - Gray
  neutral: {
    bg: '#F3F4F6',            // Gray 100
    icon: '#374151',          // Gray 700
    iconOnDark: '#FFFFFF',    // White icon
    solid: '#6B7280',         // Gray 500
    text: '#374151',          // Gray 700
    border: '#D1D5DB',        // Gray 300
  },
};

// KPI Card Colors - High Contrast Icons
export const kpiColors = {
  appointments: {
    bg: '#EFF6FF',            // Blue 50 - Very light
    icon: '#1D4ED8',          // Blue 700 - Dark icon
    gradient: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
    solid: '#3B82F6',
    text: '#1E40AF',
  },
  patients: {
    bg: '#F0FDF4',            // Green 50
    icon: '#15803D',          // Green 700 - Dark icon
    gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    solid: '#10B981',
    text: '#166534',
  },
  reports: {
    bg: '#FEF3C7',            // Amber 50
    icon: '#B45309',          // Amber 700 - Dark icon
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    solid: '#F59E0B',
    text: '#92400E',
  },
  notifications: {
    bg: '#F5F3FF',            // Purple 50
    icon: '#6D28D9',          // Purple 700 - Dark icon
    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
    solid: '#8B5CF6',
    text: '#5B21B6',
  },
  revenue: {
    bg: '#FFF7ED',            // Orange 50
    icon: '#EA580C',          // Orange 600 - Dark icon
    gradient: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
    solid: '#F97316',
    text: '#9A3412',
  },
  analytics: {
    bg: '#FDF2F8',            // Pink 50
    icon: '#BE185D',          // Pink 700 - Dark icon
    gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
    solid: '#EC4899',
    text: '#9D174D',
  },
};

// Workflow Status Colors
export const workflowColors = {
  pending: {
    bg: '#FEF3C7',
    icon: '#D97706',
    badge: '#F59E0B',
    text: '#92400E',
  },
  accepted: {
    bg: '#DCFCE7',
    icon: '#15803D',
    badge: '#10B981',
    text: '#166534',
  },
  inProgress: {
    bg: '#DBEAFE',
    icon: '#1D4ED8',
    badge: '#3B82F6',
    text: '#1E3A8A',
  },
  visited: {
    bg: '#E0E7FF',
    icon: '#4338CA',
    badge: '#6366F1',
    text: '#3730A3',
  },
  completed: {
    bg: '#DCFCE7',
    icon: '#166534',
    badge: '#10B981',
    text: '#14532D',
  },
  cancelled: {
    bg: '#FEE2E2',
    icon: '#DC2626',
    badge: '#EF4444',
    text: '#991B1B',
  },
  noShow: {
    bg: '#FEE2E2',
    icon: '#DC2626',
    badge: '#EF4444',
    text: '#991B1B',
  },
};

// Avatar Colors - High Contrast Backgrounds
export const avatarColors = {
  doctor: {
    bg: '#EFF6FF',            // Blue 50
    icon: '#1D4ED8',          // Blue 700
    solid: '#3B82F6',         // Blue 500
  },
  officer: {
    bg: '#FEF3C7',            // Amber 50
    icon: '#B45309',          // Amber 700
    solid: '#F59E0B',         // Amber 500
  },
  admin: {
    bg: '#F5F3FF',            // Purple 50
    icon: '#6D28D9',          // Purple 700
    solid: '#8B5CF6',         // Purple 500
  },
  patient: {
    bg: '#F0FDF4',            // Green 50
    icon: '#15803D',          // Green 700
    solid: '#10B981',         // Green 500
  },
};

// Priority Colors
export const priorityColors = {
  high: {
    bg: '#FEE2E2',            // Red 100
    icon: '#DC2626',          // Red 600
    text: '#991B1B',
    border: '#EF4444',
  },
  medium: {
    bg: '#FFEDD5',            // Orange 100
    icon: '#EA580C',          // Orange 600
    text: '#9A3412',
    border: '#F97316',
  },
  low: {
    bg: '#DCFCE7',            // Green 100
    icon: '#15803D',          // Green 700
    text: '#166534',
    border: '#10B981',
  },
};

// Utility function to get status color
export const getStatusColor = (status: string) => {
  const statusMap: Record<string, keyof typeof statusColors> = {
    'PENDING': 'pending',
    'PENDING_APPROVAL': 'pending',
    'ACCEPTED': 'accepted',
    'CONFIRMED': 'accepted',
    'IN_PROGRESS': 'inProgress',
    'VISITED': 'accepted',
    'COMPLETED': 'completed',
    'CANCELLED': 'cancelled',
    'REJECTED': 'cancelled',
    'NO_SHOW': 'cancelled',
    'ERROR': 'error',
    'WARNING': 'warning',
    'INFO': 'info',
    'SUCCESS': 'success',
  };

  return statusColors[statusMap[status.toUpperCase()] || 'neutral'];
};

// Utility function to get KPI color
export const getKpiColor = (type: keyof typeof kpiColors) => {
  return kpiColors[type] || kpiColors.appointments;
};

// Utility function to get workflow color
export const getWorkflowColor = (status: string) => {
  return workflowColors[status.toLowerCase() as keyof typeof workflowColors] || workflowColors.pending;
};

// Utility function to get avatar color
export const getAvatarColor = (role: keyof typeof avatarColors) => {
  return avatarColors[role] || avatarColors.patient;
};

// Utility function to get priority color
export const getPriorityColor = (priority: keyof typeof priorityColors) => {
  return priorityColors[priority] || priorityColors.medium;
};

export default {
  statusColors,
  kpiColors,
  workflowColors,
  avatarColors,
  priorityColors,
  getStatusColor,
  getKpiColor,
  getWorkflowColor,
  getAvatarColor,
  getPriorityColor,
};